/* START_MODULE_CONTRACT
 * PURPOSE: Поля настройки периодичности накладного расхода (UC-013).
 * SCOPE: React UI для DAILY / INTERVAL / WEEKLY recurrence.
 * DEPENDS: M-FE-PROJECTS, overheadConstants
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * OverheadRecurrenceFields - controlled recurrence form section
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: компактная вёрстка, стили ToggleButtonGroup под тему.
 * LAST_CHANGE: 2026-07-03 - UC-013: UI периодичности в стиле календаря.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import {
  DAY_OF_WEEK_OPTIONS,
  DEFAULT_INTERVAL_DAYS,
  DEFAULT_OVERHEAD_RECURRENCE_TYPE,
  OVERHEAD_RECURRENCE_TYPE_OPTIONS,
  OVERHEAD_RECURRENCE_TYPES,
} from '../../../models/overheadConstants';

/* START_METHOD_OverheadRecurrenceFields */
/* START_CONTRACT: OverheadRecurrenceFields
 * PURPOSE: Controlled-блок выбора типа периодичности и связанных полей.
 * INPUTS: { recurrenceType, intervalDays, daysOfWeek, onChange, disabled }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: onChange callbacks.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: OverheadRecurrenceFields
 */
export default function OverheadRecurrenceFields({
  recurrenceType,
  intervalDays,
  daysOfWeek,
  onRecurrenceTypeChange,
  onIntervalDaysChange,
  onDaysOfWeekChange,
  disabled = false,
}) {
  const type = recurrenceType ?? DEFAULT_OVERHEAD_RECURRENCE_TYPE;

  /* START_METHOD_handleTypeChange */
  const handleTypeChange = (event) => {
    const nextType = event.target.value;
    onRecurrenceTypeChange(nextType);
    if (nextType === OVERHEAD_RECURRENCE_TYPES.INTERVAL && !intervalDays) {
      onIntervalDaysChange(String(DEFAULT_INTERVAL_DAYS));
    }
    if (nextType === OVERHEAD_RECURRENCE_TYPES.WEEKLY && (!daysOfWeek || daysOfWeek.length === 0)) {
      onDaysOfWeekChange([1, 2, 3, 4, 5]);
    }
  };
  /* END_METHOD_handleTypeChange */

  /* START_METHOD_handleDaysToggle */
  const handleDaysToggle = (_event, newDays) => {
    if (newDays.length === 0) {
      return;
    }
    onDaysOfWeekChange(newDays.sort((a, b) => a - b));
  };
  /* END_METHOD_handleDaysToggle */

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        fullWidth
        label="Периодичность"
        value={type}
        onChange={handleTypeChange}
        size="small"
        required
        disabled={disabled}
      >
        {OVERHEAD_RECURRENCE_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {type === OVERHEAD_RECURRENCE_TYPES.INTERVAL && (
        <TextField
          fullWidth
          label="Каждые N дней"
          type="number"
          value={intervalDays ?? ''}
          onChange={(e) => onIntervalDaysChange(e.target.value)}
          size="small"
          required
          helperText="Например, 2 — через день"
          disabled={disabled}
          slotProps={{
            htmlInput: { min: 1, max: 365, step: 1 },
          }}
        />
      )}

      {type === OVERHEAD_RECURRENCE_TYPES.WEEKLY && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Дни недели
          </Typography>
          <ToggleButtonGroup
            value={daysOfWeek ?? []}
            onChange={handleDaysToggle}
            aria-label="Дни недели"
            size="small"
            disabled={disabled}
            sx={{
              flexWrap: 'wrap',
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px !important',
                mx: 0,
                px: 1.25,
                py: 0.5,
                minWidth: 40,
                '&.Mui-selected': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
                  },
                },
              },
            }}
          >
            {DAY_OF_WEEK_OPTIONS.map((day) => (
              <ToggleButton key={day.value} value={day.value} aria-label={day.label}>
                {day.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
}
/* END_METHOD_OverheadRecurrenceFields */
